(() => {
  const elementName = 'cart-free-gift';

  if (customElements.get(elementName)) return;

  const syncState = {
    busy: false,
    pending: false,
  };

  class CartFreeGift extends HTMLElement {
    connectedCallback() {
      Promise.resolve().then(() => this.reconcile());
    }

    async reconcile() {
      if (!this.isConnected) return;

      if (syncState.busy) {
        syncState.pending = true;
        return;
      }

      syncState.busy = true;

      try {
        const action = this.getRequiredAction();

        if (!action) return;

        const response = await this.updateCart(action);
        this.renderSections(response.sections || {});

        // A newly rendered manager performs one final check after this request.
        // This also handles switching the selected gift without racing two requests.
        syncState.pending = true;
        await this.publishCartUpdate(response);
      } catch (error) {
        this.showError(error);
      } finally {
        syncState.busy = false;
        this.schedulePendingReconcile();
      }
    }

    getRequiredAction() {
      const enabled = this.dataset.enabled === 'true';
      const hasQualifyingItem = this.dataset.hasQualifyingItem === 'true';
      const defaultVariantId = this.dataset.variantId || '';
      const threshold = Number.parseInt(this.dataset.threshold || '0', 10);
      const qualifyingTotal = Number.parseInt(this.dataset.qualifyingTotal || '0', 10);
      const allowedVariantIds = this.getAllowedVariantIds();
      const giftLines = Array.from(this.querySelectorAll('[data-free-gift-line]')).map((line) => ({
        key: line.dataset.lineKey,
        variantId: line.dataset.variantId,
        quantity: Number.parseInt(line.dataset.quantity || '0', 10),
      }));
      const shouldHaveGift =
        enabled &&
        hasQualifyingItem &&
        defaultVariantId !== '' &&
        Number.isFinite(threshold) &&
        Number.isFinite(qualifyingTotal) &&
        threshold >= 0 &&
        qualifyingTotal >= threshold;
      const updates = {};

      if (!shouldHaveGift) {
        giftLines.forEach((line) => {
          if (line.key) updates[line.key] = 0;
        });
      } else {
        let keptGift = false;

        giftLines.forEach((line) => {
          if (!line.key) return;

          if (!allowedVariantIds.has(line.variantId) || keptGift) {
            updates[line.key] = 0;
            return;
          }

          keptGift = true;
          if (line.quantity !== 1) updates[line.key] = 1;
        });

        if (Object.keys(updates).length === 0 && !keptGift) {
          return this.getGiftAddAction(defaultVariantId);
        }
      }

      if (Object.keys(updates).length === 0) return null;

      return {
        endpoint: this.getRoute('cart_update_url', '/cart/update.js'),
        body: { updates },
      };
    }

    getAllowedVariantIds() {
      const variantIds = new Set(
        Array.from(this.querySelectorAll('[data-free-gift-option]'))
          .map((option) => option.dataset.variantId)
          .filter(Boolean)
      );

      if (variantIds.size === 0 && this.dataset.variantId) variantIds.add(this.dataset.variantId);

      return variantIds;
    }

    getGiftAddAction(variantId) {
      return {
        endpoint: this.getRoute('cart_add_url', '/cart/add.js'),
        body: {
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _cart_free_gift: 'true',
                _oid: '1225032_pYhXEacxFM'
              },
            },
          ],
        },
      };
    }

    async changeVariant(selector) {
      const lineKey = selector.dataset.lineKey || '';
      const currentVariantId = selector.dataset.currentVariantId || '';
      const nextVariantId = selector.value || '';

      if (!lineKey || !nextVariantId || nextVariantId === currentVariantId) return;

      if (!this.getAllowedVariantIds().has(nextVariantId) || syncState.busy) {
        selector.value = currentVariantId;
        if (syncState.busy) syncState.pending = true;
        return;
      }

      syncState.busy = true;
      selector.disabled = true;
      selector.closest('.ms-cart-item__gift-variant')?.classList.add('is-loading');

      try {
        await this.updateCart(
          {
            endpoint: this.getRoute('cart_update_url', '/cart/update.js'),
            body: { updates: { [lineKey]: 0 } },
          },
          false
        );

        const response = await this.updateCart(this.getGiftAddAction(nextVariantId));
        this.renderSections(response.sections || {});
        syncState.pending = true;
        await this.publishCartUpdate(response);
      } catch (error) {
        selector.value = currentVariantId;
        this.showError(error);
      } finally {
        if (selector.isConnected) {
          selector.disabled = false;
          selector.closest('.ms-cart-item__gift-variant')?.classList.remove('is-loading');
        }

        syncState.busy = false;
        this.schedulePendingReconcile();
      }
    }

    schedulePendingReconcile() {
      if (!syncState.pending) return;

      syncState.pending = false;
      window.requestAnimationFrame(() => {
        const currentManager = document.querySelector(elementName);
        if (currentManager) currentManager.reconcile();
      });
    }

    getRoute(routeName, fallback) {
      return window.routes && window.routes[routeName] ? window.routes[routeName] : fallback;
    }

    async updateCart(action, includeSections = true) {
      const body = { ...action.body };

      if (includeSections) {
        body.sections = ['cart-drawer', 'cart-icon-bubble'];
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(action.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      let result;

      try {
        result = await response.json();
      } catch (error) {
        throw new Error('The cart returned an invalid response.');
      }

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'The free gift could not be updated.');
      }

      return result;
    }

    renderSections(sections) {
      if (sections['cart-drawer']) this.renderCartDrawer(sections['cart-drawer']);
      if (sections['cart-icon-bubble']) this.renderCartIcon(sections['cart-icon-bubble']);
    }

    renderCartDrawer(html) {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const incomingDrawer = parsedHtml.querySelector('cart-drawer');
      const incomingInner = incomingDrawer && incomingDrawer.querySelector('.drawer__inner');
      const currentDrawer = document.querySelector('cart-drawer');
      const currentInner = currentDrawer && currentDrawer.querySelector('.drawer__inner');

      if (!incomingDrawer || !incomingInner || !currentDrawer || !currentInner) return;

      const restoreDrawerFocus = currentDrawer.classList.contains('active') && currentInner.contains(document.activeElement);
      const previousScrollTop = currentInner.querySelector('.ms-cart-drawer__scroll')?.scrollTop || 0;
      currentInner.replaceWith(document.importNode(incomingInner, true));
      currentDrawer.classList.toggle('is-empty', incomingDrawer.classList.contains('is-empty'));

      const currentScrollContainer = currentDrawer.querySelector('.ms-cart-drawer__scroll');
      if (currentScrollContainer) currentScrollContainer.scrollTop = previousScrollTop;

      if (restoreDrawerFocus) {
        const closeButton = currentDrawer.querySelector('.drawer__close');
        if (closeButton) closeButton.focus({ preventScroll: true });
      }
    }

    renderCartIcon(html) {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const incomingSection = parsedHtml.querySelector('.shopify-section');
      const currentIcon = document.getElementById('cart-icon-bubble');

      if (incomingSection && currentIcon) currentIcon.innerHTML = incomingSection.innerHTML;
    }

    async publishCartUpdate(cartData) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      try {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-free-gift',
          cartData,
        });
      } catch (error) {
        console.error('Cart free gift refresh failed:', error);
      }
    }

    showError(error) {
      const errorContainer = document.getElementById('CartDrawer-CartErrors');
      const fallbackMessage = "We couldn't update your free gift. Please refresh the page.";

      if (errorContainer) errorContainer.textContent = error.message || fallbackMessage;
      console.error('Cart free gift update failed:', error);
    }
  }

  customElements.define(elementName, CartFreeGift);

  document.addEventListener(
    'change',
    (event) => {
      const selector = event.target.closest?.('[data-free-gift-variant-selector]');

      if (!selector) return;

      // Prevent Dawn's cart quantity handler from treating this select as a quantity input.
      event.stopPropagation();

      const manager = document.querySelector(elementName);

      if (manager) {
        manager.changeVariant(selector);
      } else {
        selector.value = selector.dataset.currentVariantId || selector.value;
      }
    },
    true
  );
})();
